/* ==========================================================================
   H3O Comparison Chart — tabbed animated bars + count-up values.
   Bars grow when the widget scrolls into view, and re-animate on tab switch.
   Tab 3 ("monthly") swaps row labels for their stack labels, because covering
   both jobs from a single-job product means buying a second product.
   ========================================================================== */

if (!customElements.get('h3o-chart')) {
  customElements.define(
    'h3o-chart',
    class H3OChart extends HTMLElement {
      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('.h3o-chart__tab'));
        this.rows = Array.from(this.querySelectorAll('.h3o-chart__row'));
        this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.armed = false;

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab));
        });

        if (this.reduceMotion || !('IntersectionObserver' in window)) {
          this.armed = true;
          this.render(this.tabs[0]);
          return;
        }

        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting && !this.armed) {
                this.armed = true;
                this.render(this.activeTab());
                io.unobserve(this);
              }
            });
          },
          { threshold: 0.35 }
        );
        io.observe(this);
      }

      activeTab() {
        return this.tabs.find((t) => t.classList.contains('is-active')) || this.tabs[0];
      }

      select(tab) {
        this.tabs.forEach((t) => {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        if (this.armed) this.render(tab);
      }

      format(value, unit, decimals) {
        if (unit === '$') return '$' + value.toFixed(2);
        if (unit === 'mg') return value.toLocaleString('en-US') + 'mg';
        /* grams: trim trailing zero */
        const s = decimals > 0 ? value.toFixed(decimals).replace(/\.0$/, '') : String(Math.round(value));
        return s + unit;
      }

      render(tab) {
        const metric = tab.dataset.metric;
        const unit = tab.dataset.unit;
        const decimals = parseInt(tab.dataset.decimals, 10) || 0;

        const values = this.rows.map((r) => parseFloat(r.dataset[metric]) || 0);
        const max = Math.max.apply(null, values) || 1;

        this.rows.forEach((row, i) => {
          const value = values[i];
          const fill = row.querySelector('[data-chart-fill]');
          const out = row.querySelector('[data-chart-value]');
          const labelEl = row.querySelector('[data-chart-label]');

          /* tab 3 shows the two-product stack required to match H3O */
          if (labelEl) {
            labelEl.textContent = metric === 'month' ? row.dataset.stackLabel : row.dataset.label;
          }

          const pct = Math.max(2.5, (value / max) * 100);

          if (this.reduceMotion) {
            row.style.setProperty('--h3o-bar', pct.toFixed(2));
            if (out) {
              out.textContent = this.format(value, unit, decimals);
              out.classList.toggle('is-zero', value === 0);
            }
            return;
          }

          /* reset then grow (forces the width transition to replay) */
          row.style.setProperty('--h3o-bar', '0');
          if (fill) void fill.offsetWidth;
          row.style.setProperty('--h3o-bar', pct.toFixed(2));

          /* count-up */
          if (out) {
            out.classList.toggle('is-zero', value === 0);
            const duration = 850;
            let start = null;
            const step = (ts) => {
              if (!start) start = ts;
              const p = Math.min((ts - start) / duration, 1);
              const eased = 1 - Math.pow(1 - p, 3);
              out.textContent = this.format(value * eased, unit, decimals);
              if (p < 1) requestAnimationFrame(step);
              else out.textContent = this.format(value, unit, decimals);
            };
            requestAnimationFrame(step);
          }
        });
      }
    }
  );
}
